/**
 * Validated chart palette for the Financials tab.
 *
 * Five fixed slots, snapped from the app's --chart-* theme tokens to steps that
 * pass the dataviz six-check validator (lightness band, chroma floor, CVD
 * separation, normal-vision floor, 3:1 contrast) on BOTH surfaces: white cards
 * in light mode and hsl(220 25% 12%) cards in dark. The raw tokens fail (light
 * navy under the lightness band, dark amber/orange over it) — do not swap these
 * back to hsl(var(--chart-N)) without re-running the validator.
 *
 * Identity is fixed and shared across every widget on the tab: actual/collected
 * = navy, backlog = teal, upside/starred = amber, MVA = purple, other collected
 * = orange. Five is the ceiling, not an accident: seven slots could not clear
 * CVD separation (navy vs purple came out at ΔE 2.7 for deuteranopes), which is
 * why the donut groups Retainer/Consulting/Other into one slice instead of
 * giving each its own hue. Per-type detail lives in the entries panel.
 *
 * Donut ring order is navy → orange → purple → teal → amber, chosen so no two
 * adjacent arcs are a weak CVD pair. Reordering the ring means re-validating.
 */
export const FIN_COLORS = {
  actual: { light: "#3b65ba", dark: "#406abf" },
  other: { light: "#bc5e2f", dark: "#cf6a3a" },
  mva: { light: "#9540bf", dark: "#9f53c6" },
  backlog: { light: "#339977", dark: "#2f9d78" },
  upside: { light: "#a5731d", dark: "#bd8a36" },
} as const;

/** Literal class strings so Tailwind's JIT compiles them (never build these dynamically). */
export const FIN_CHIP_CLASS = {
  actual: "bg-[#3b65ba] dark:bg-[#406abf]",
  other: "bg-[#bc5e2f] dark:bg-[#cf6a3a]",
  mva: "bg-[#9540bf] dark:bg-[#9f53c6]",
  backlog: "bg-[#339977] dark:bg-[#2f9d78]",
  upside: "bg-[#a5731d] dark:bg-[#bd8a36]",
} as const;

export const FIN_TEXT_CLASS = {
  upside: "text-[#a5731d] dark:text-[#bd8a36]",
} as const;

/**
 * Slot colors as TEXT color, so a mark can render itself with `currentColor`.
 * That is what lets the projected segments carry a hatch pattern in both themes
 * without duplicating every gradient for dark mode.
 */
export const FIN_INK_CLASS = {
  actual: "text-[#3b65ba] dark:text-[#406abf]",
  other: "text-[#bc5e2f] dark:text-[#cf6a3a]",
  mva: "text-[#9540bf] dark:text-[#9f53c6]",
  backlog: "text-[#339977] dark:text-[#2f9d78]",
  upside: "text-[#a5731d] dark:text-[#bd8a36]",
} as const;

/**
 * Diagonal hatch drawn in `currentColor`. Projected money is hatched and
 * collected money is solid — texture, not hue, carries the difference, so the
 * five validated slot colors keep meaning "which bucket" and nothing else.
 * (Texture as a secondary encoding also survives colorblindness and print.)
 */
export const FIN_HATCH =
  "bg-[repeating-linear-gradient(45deg,currentColor_0_2px,transparent_2px_5px)]";
